import React, { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Brand, brandFor } from './brands';

/**
 * Which brand the page wears: a white-label workshop's, or none (= Mize Pro).
 *
 * - Signed in: the user's `brandKey` from the server (workshop users only).
 * - Customer and supplier pages: whatever their own server data names (usePageBrand);
 *   never the signed-in user's — a Pit Stop phone opening a Mize payment link sees Mize.
 * - The login page can't know the workshop yet: it shows the brand this device last
 *   signed in with, or `?brand=` (a workshop's own sign-in link).
 *
 * While a brand shows, `<html data-brand="…">` switches on its layer in brands.css, and
 * the tab title, tab icon and home-screen icon are swapped (Mize's are put back after).
 */

// Also read by the pre-paint script in public/index.html — keep the two in step.
export const REMEMBER_KEY = 'sharay-brand';
const PUBLIC_PREFIXES = ['/feedback/', '/quote/', '/jc/', '/pay/', '/labour/', '/d/'];

interface BrandContextValue {
  brand: Brand | null;
  setPageBrand: (key: string | null | undefined) => void;
}

const BrandContext = createContext<BrandContextValue>({ brand: null, setPageBrand: () => {} });

function readRemembered(): string | null {
  try { return localStorage.getItem(REMEMBER_KEY); } catch { return null; }
}

function writeRemembered(key: string | null) {
  try {
    if (key) localStorage.setItem(REMEMBER_KEY, key);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch { /* private mode: the login page just shows Mize */ }
}

const SAVED = 'data-mize-value';
const FONT_LINK_ID = 'brand-fonts';
const MIZE_TITLE = 'Mize Pro'; // public/index.html <title>

function applyBrandToDocument(brand: Brand | null) {
  const root = document.documentElement;
  if (brand) root.setAttribute('data-brand', brand.key);
  else root.removeAttribute('data-brand');

  // Swap an attribute while branded; the first swap keeps Mize's value to put back.
  const swap = (selector: string, attr: 'href' | 'content', value: string | null) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (!el.hasAttribute(SAVED)) el.setAttribute(SAVED, el.getAttribute(attr) || '');
      el.setAttribute(attr, value ?? el.getAttribute(SAVED) ?? '');
    });
  };
  swap('link[rel~="icon"]', 'href', brand ? brand.favicon : null);
  swap('link[rel="apple-touch-icon"]', 'href', brand ? brand.appleTouchIcon : null);
  swap('meta[name="apple-mobile-web-app-title"]', 'content', brand ? brand.name.en : null);

  // A constant, not "the title before we changed it": the pre-paint script in index.html
  // may already have set the brand's title by then.
  document.title = brand ? brand.name.en : MIZE_TITLE;

  let fonts = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (brand?.fontsHref) {
    if (!fonts) {
      fonts = document.createElement('link');
      fonts.id = FONT_LINK_ID;
      fonts.rel = 'stylesheet';
      document.head.appendChild(fonts);
    }
    if (fonts.getAttribute('href') !== brand.fontsHref) fonts.setAttribute('href', brand.fontsHref);
  } else if (fonts) {
    fonts.remove();
  }
}

export const BrandProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const [pageBrand, setPageBrand] = useState<string | null | undefined>(undefined);

  const urlBrand = pathname === '/login' ? new URLSearchParams(search).get('brand') : null;
  useEffect(() => {
    if (brandFor(urlBrand)) writeRemembered(urlBrand);
  }, [urlBrand]);
  // Whoever signs in decides what this device's login page shows next time.
  useEffect(() => {
    if (user) writeRemembered(brandFor(user.brandKey) ? user.brandKey! : null);
  }, [user]);

  const isPublic = PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
  let key: string | null | undefined;
  if (pageBrand !== undefined) key = pageBrand;
  else if (isPublic) key = null;
  else if (user) key = user.brandKey;
  else key = brandFor(urlBrand) ? urlBrand : readRemembered();
  const brand = brandFor(key);

  useLayoutEffect(() => applyBrandToDocument(brand), [brand]);

  const value = useMemo(() => ({ brand, setPageBrand }), [brand]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

/** The brand on show, or null for Mize Pro. */
export function useBrand(): Brand | null {
  return useContext(BrandContext).brand;
}

/**
 * A customer page wears the brand its own server data names, while it is open.
 * `undefined` = not loaded yet (changes nothing); `null` = Mize Pro.
 */
export function usePageBrand(key: string | null | undefined) {
  const { setPageBrand } = useContext(BrandContext);
  useEffect(() => {
    if (key === undefined) return undefined;
    setPageBrand(key);
    return () => setPageBrand(undefined);
  }, [key, setPageBrand]);
}
